import useAuth from '@lib/hooks/useAuth';
import useFeature from '@lib/hooks/useFeature';
import { isEnrolledInRewards } from '@components/CheckoutControls/Rewards/rewardSpendNudge';

export interface RewardsValidationGate {
    /**
     * Whether the rewards screen should be gated behind the account-validation modal.
     *
     * True when ALL hold:
     *   - `mfa-account-verification` flag is ON,
     *   - the account is subject to verification (`is_phone_verification_enabled` is not
     *     explicitly false),
     *   - the user is logged in, AND
     *   - the user's phone is EXPLICITLY not validated (`isPhoneValidated === false`).
     *
     * Enrollment is deliberately NOT part of this: unverified means no rewards, whether or not
     * the member already has a rewards account. An enrolled-but-unverified member previously
     * sailed past this gate straight into the hang webview — the account exists but they can't
     * be trusted to own the phone behind it until verification lands.
     *
     * The `=== false` is deliberate: `isPhoneValidated` is optional and is `undefined`
     * until the first profile-info resolves. Treating `undefined` as "not validated"
     * would gate a verified user for one frame on cold start — a visible flash of the
     * validation modal at someone who already verified.
     *
     * When true, callers show the logged-out rewards screen (blurred) behind a non-dismissible
     * "validate your account" modal, and must NOT render rewards content — nor the legacy hang
     * iframe, which is still reachable on web with `rewards-hub-v1-web` off. With the flag off
     * this is always false → existing behavior is untouched.
     */
    showValidationGate: boolean;

    /**
     * True when the AV flag is ON, the user is logged in, and their phone IS validated.
     *
     * NO LONGER CONSUMED (2026-08-31). It existed to force the native hub for a verified user
     * with no magic link, because routing then keyed on `!!loyalty?.magic_link` and would
     * otherwise have dropped them into the legacy hang webview. Both platforms now mount the
     * hub unconditionally (mobile) or on `rewards-hub-v1-web` alone (web), so nothing needs
     * forcing. Retained only so the legacy routing can be restored without re-deriving it;
     * delete once that revert path is confirmed unnecessary.
     */
    forceHubForVerified: boolean;

    /**
     * Whether to hide the rewards entry-point affordances: Home's scan/claim button (and its
     * expanded-tray scrim) and the earned-rewards counter badge on the Rewards tab icon.
     *
     * An entry point requires a phone that is verified AND a rewards account. Hidden when the
     * global flag is on and the user is logged in and EITHER
     *   - the phone is not verified (enrolled or not) AND verification applies to this account
     *     (`is_phone_verification_enabled` is not explicitly false), OR
     *   - they have no rewards account (`!isEnrolledInRewards`) — which applies regardless of
     *     the per-user switch, since the switch exempts an account from phone verification,
     *     not from needing a rewards account to scan into or count against.
     *
     * Both halves have to hold for an entry point to mean anything. Scan/claim act directly on
     * a rewards account, and the counter badge advertises rewards earned against one: an
     * unverified member can't use the account even when enrolled, and a verified member with no
     * account has nothing to scan into or count.
     *
     * This is a superset of `showValidationGate`: everyone sent to the validation screen also
     * loses these affordances, plus the verified-but-not-enrolled member (who reaches the real
     * rewards screen, just with nothing to scan). The enrollment half is the only difference
     * between the two fields — keep it here rather than re-deriving it at each call site.
     *
     * Note the `!user.isPhoneValidated` here vs. `=== false` in `showValidationGate`: on these
     * surfaces `undefined` (profile-info unresolved) counts as unverified. The tradeoff inverts
     * when hiding rather than showing — hiding then revealing an affordance is a far less
     * jarring cold-start artifact than flashing a validation modal at an already-verified user.
     */
    hideRewardsEntryPoint: boolean;
}

/**
 * Account-validation gating for the rewards surfaces (`mfa-account-verification`). Returns both
 * the pre-validation gate and the post-validation hub-forcing flag; consumers destructure the
 * field(s) they need. Reads the flag + auth once so call sites that need both don't double-read.
 */
const useRewardsValidationGate = (): RewardsValidationGate => {
    const isAccVerificationEnabled = useFeature('mfa-account-verification', false);
    const { user, loyalty } = useAuth();

    /**
     * Account verification applies only when the global rollout flag AND the per-user backend
     * switch are both on. `is_phone_verification_enabled: false` means this account is not
     * subject to phone verification at all, so it keeps full rewards access even while
     * `isPhoneValidated` is false.
     *
     * `!== false`, not `=== true`: the field is `undefined` until profile-info resolves (and on
     * backends that predate it). Undefined must fall through to the pre-existing
     * `isPhoneValidated` rules rather than silently un-gating every user on cold start.
     */
    const appliesToUser = isAccVerificationEnabled && user?.isPhoneVerificationEnabled !== false;

    const isLoggedInWithFlag = appliesToUser && !!user;

    // Enrollment is intentionally not consulted — unverified means no rewards either way.
    // `=== false`, not `!`: `undefined` (profile-info not resolved yet) must not gate.
    const showValidationGate = isLoggedInWithFlag && user.isPhoneValidated === false;

    // Entry points require verified AND enrolled — see `hideRewardsEntryPoint` above for why
    // this is stricter than the tab destination, and why `undefined` counts as unverified.
    //
    // The two halves are gated differently on purpose. The VERIFICATION half only applies when
    // account verification applies to this user (`isLoggedInWithFlag`). The ENROLLMENT half
    // applies to any logged-in user under the flag, including one whose backend switch is off:
    // `is_phone_verification_enabled: false` exempts the account from phone verification, not
    // from needing a rewards account. Scan/claim acts on that account and the badge counts
    // rewards earned against it, so with no account there is still nothing to show.
    const hideRewardsEntryPoint =
        (isLoggedInWithFlag && !user.isPhoneValidated) ||
        (isAccVerificationEnabled && !!user && !isEnrolledInRewards(loyalty));

    return {
        showValidationGate,
        forceHubForVerified: isLoggedInWithFlag && !!user.isPhoneValidated,
        hideRewardsEntryPoint
    };
};

export default useRewardsValidationGate;
